<template>
  <section class="reviews" id="reviews">
    <div class="reviews-inner">
      <div class="reviews-header">
        <h2>Wat onze patiënten zeggen</h2>
      </div>

      <div class="reviews-layout">
        <div class="reviews-photo-column">
          <img :src="reviewPhoto" alt="Tandzorg Gestel team" class="reviews-photo" loading="lazy">
        </div>

        <div v-if="loading" class="state-box reviews-state">
          Reviews laden...
        </div>

        <div v-else-if="visibleReviews.length" class="reviews-grid">
          <article v-for="(review, index) in visibleReviews" :key="`${review.id}-${index}`" class="review-card">
            <div class="review-top">
              <div>
                <div class="review-author">{{ review.author }}</div>
                <div class="review-meta">{{ review.date }}</div>
              </div>
              <div class="review-rating">{{ review.stars }}</div>
            </div>

            <p class="review-text">{{ review.text }}</p>
          </article>
        </div>

        <div v-else class="state-box reviews-state">
          <p>Geen reviews beschikbaar op dit moment.</p>
          <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { GoogleReviewsService } from '@/services/GoogleReviewsService'
import reviewPhoto from '@/assets/images/Inleiding.jpg'

export default {
  name: 'ReviewsSection',
  data() {
    return {
      reviewPhoto,
      reviews: [],
      loading: true,
      errorMessage: '',
      currentStartIndex: 0,
      rotateIntervalId: null,
      visibleCount: 4
    }
  },
  computed: {
    visibleReviews() {
      if (this.reviews.length <= this.visibleCount) {
        return this.reviews
      }

      const selected = []
      for (let i = 0; i < this.visibleCount; i += 1) {
        const index = (this.currentStartIndex + i) % this.reviews.length
        selected.push(this.reviews[index])
      }

      return selected
    }
  },
  async mounted() {
    const service = new GoogleReviewsService()
    const result = await service.fetchReviews()

    this.reviews = (result.reviews || [])
      .map((review) => service.formatReview(review))
      .slice(0, 12)
    this.errorMessage = result.errorMessage || ''
    this.loading = false

    if (this.reviews.length > this.visibleCount) {
      this.rotateIntervalId = setInterval(() => {
        this.currentStartIndex = (this.currentStartIndex + 1) % this.reviews.length
      }, 5000)
    }
  },
  beforeUnmount() {
    if (this.rotateIntervalId) {
      clearInterval(this.rotateIntervalId)
      this.rotateIntervalId = null
    }
  }
}
</script>

<style scoped>
.reviews {
  background:
    radial-gradient(circle at top left, rgba(16, 123, 255, 0.08), transparent 28%),
    linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
  padding: 4rem 0;
}

.reviews-inner {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.reviews-header {
  text-align: center;
  max-width: 720px;
  margin: 0 auto 2rem;
}

.reviews-header h2 {
  margin: 0;
  font-size: 4.2rem;
  font-weight: bolder;
  color: var(--black);
}

.reviews-layout {
  display: grid;
  grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
  gap: 2rem;
  align-items: start;
}

.reviews-photo-column {
  display: flex;
  align-items: stretch;
}

.reviews-photo {
  width: 100%;
  height: 100%;
  min-height: 320px;
  object-fit: cover;
  border-radius: 22px;
  box-shadow: 0 12px 30px rgba(17, 40, 90, 0.12);
  border: 1px solid rgba(16, 123, 255, 0.08);
}

.reviews-grid {
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.25rem;
}

.reviews-state {
  min-width: 0;
}

.review-card {
  background: #fff;
  border-radius: 18px;
  padding: 1.5rem;
  box-shadow: 0 12px 30px rgba(17, 40, 90, 0.08);
  border: 1px solid rgba(16, 123, 255, 0.08);
}

.review-top {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.review-author {
  font-weight: 700;
  color: var(--black);
  font-size: 1.4rem;
}

.review-meta {
  margin-top: 0.25rem;
  font-size: 1.05rem;
  color: var(--light-color);
}

.review-rating {
  color: #f4b400;
  font-size: 1.2rem;
  white-space: nowrap;
}

.review-text {
  margin: 0;
  color: var(--black);
  line-height: 1.8;
  font-size: 1.25rem;
}

.state-box {
  max-width: 720px;
  margin: 0 auto;
  padding: 1.5rem;
  text-align: center;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.8);
  color: var(--light-color);
  box-shadow: 0 10px 24px rgba(17, 40, 90, 0.06);
}

.state-box p {
  margin: 0.25rem 0;
}

.error-text {
  color: #b84a4a;
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .reviews {
    padding: 3rem 0;
  }

  .reviews-inner {
    padding: 0 1rem;
  }

  .review-card {
    padding: 1.25rem;
  }

  .reviews-header h2 {
    font-size: 3rem;
  }

  .reviews-layout {
    grid-template-columns: 1fr;
  }

  .reviews-photo {
    min-height: 240px;
  }

  .review-author {
    font-size: 1.3rem;
  }

  .review-meta {
    font-size: 1rem;
  }

  .review-text {
    font-size: 1.15rem;
  }
}
</style>